import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { Briefcase, ChevronDown, ChevronUp, Search, PartyPopper, Square, SquareCheck } from 'lucide-react';
import { adviserFactsheetOf } from '../../../../utils/client-factsheet';
import { avatarGenderClass, clientNameOf, tierMetaOf } from '../clientPresentation';
import { useCountUp } from './StatTiles';
import type { RecruitSuggestion, RecruitSuggestionKind } from '../recruitSuggestions';
import type { IntlLike } from '../clientPresentation';
import type { QueueClient } from '../../../../types/client';

/**
 * 今日招募 — 重點跟進 的招募版,連 markup 都是同一套(.clientv2-queue-*)。
 *
 * 不是像它,而是就是它:同樣的卡片格、同樣的訊號藥丸、同樣的剔格、同樣的
 * 「立即跟進」。顧問在兩個模式之間來回,不應該覺得自己換了一個產品 —— 換的是
 * 卡上寫什麼,不是卡長什麼樣。
 *
 * 剔格的狀態只活在這裡,和 TodayQueue 一樣:它是接下來幾分鐘的閱讀輔助,不是
 * 關於候選人的事實。一個重載之後還在的剔格,就是在宣稱自己是後者。
 */

/**
 * 訊號 —— 和業務卡一樣,九條規則歸成五組,不是九個顏色。顏色借用
 * constants/client-queue 的同一組色板,因為這兩張卡並排在同一個腦袋裡:同一個
 * 紅色在兩邊都該是「落後了」。
 */
type RecruitSignal = 'acted' | 'chance' | 'behind' | 'todo' | 'source';

const SIGNAL_OF: Record<RecruitSuggestionKind, RecruitSignal> = {
  quiz: 'acted',
  priorityNotStarted: 'chance',
  referral: 'chance',
  stalled: 'behind',
  pausedLong: 'behind',
  concern: 'todo',
  unrated: 'todo',
  fillIn: 'todo',
  sourcing: 'source',
  fallback: 'source'
};

const SIGNAL_COLOR: Record<RecruitSignal, string> = {
  acted: '#0f9b8e',
  chance: '#a8730f',
  behind: '#e2606a',
  todo: '#7c3aed',
  source: '#64748b'
};

const SIGNAL_LABEL: Record<RecruitSignal, string> = {
  acted: 'clientv2-recruit-signal-acted',
  chance: 'clientv2-recruit-signal-chance',
  behind: 'clientv2-recruit-signal-behind',
  todo: 'clientv2-recruit-signal-todo',
  source: 'clientv2-recruit-signal-source'
};

/** The line under the name — what this row is about, in three or four words. */
const KIND_TITLE: Record<RecruitSuggestionKind, string> = {
  quiz: 'clientv2-recruit-kind-quiz',
  stalled: 'clientv2-recruit-kind-stalled',
  priorityNotStarted: 'clientv2-recruit-kind-priority',
  concern: 'clientv2-recruit-kind-concern',
  unrated: 'clientv2-recruit-kind-unrated',
  pausedLong: 'clientv2-recruit-kind-paused',
  fillIn: 'clientv2-recruit-kind-fill-in',
  sourcing: 'clientv2-recruit-kind-sourcing',
  referral: 'clientv2-recruit-kind-referral',
  fallback: 'clientv2-recruit-kind-fallback'
};

export type RecruitSuggestionsProps = {
  suggestions: RecruitSuggestion[];
  /** Opens whatever this suggestion's CTA points at. */
  onSuggestion: (suggestion: RecruitSuggestion) => void;
  /** Opens the candidate's drawer, as the business card's client block does. */
  onSelectClient: (clientId: string) => void;
  /** 再聯絡看看 — the paused card's own way back into the funnel (pausedLong only). */
  onRecontact?: (client: QueueClient) => void;
  /** 移出招募名單 — the paused card's way to close the decision instead (pausedLong only). */
  onRemoveFromRecruitment?: (client: QueueClient) => void;
  adviserEmail?: string;
  intl: IntlLike;
};

const RecruitSuggestions = ({
  suggestions,
  onSuggestion,
  onSelectClient,
  onRecontact,
  onRemoveFromRecruitment,
  adviserEmail,
  intl
}: RecruitSuggestionsProps) => {
  // Closed to begin with, as TodayQueue is. Entering the mode is already a
  // whole page changing under the adviser; a card that also throws three task
  // cards open makes the arrival louder than the thing arrived at. The head
  // still carries the count and the meter, which is what the glance is for —
  // the cards are there for whoever wants them.
  const [expanded, setExpanded] = useState(false);
  const [done, setDone] = useState<Record<string, boolean>>({});
  const message = (id: string, values?: Record<string, unknown>) => intl.formatMessage({ id }, values);

  const doneCount = suggestions.filter(item => done[item.id]).length;
  const progress = suggestions.length ? Math.round((doneCount / suggestions.length) * 100) : 0;
  const animatedProgress = useCountUp(progress, 800);
  const allDone = suggestions.length > 0 && doneCount === suggestions.length;
  const expandable = suggestions.length > 0;

  /**
   * Two of the templates name a concern and its answer, which are themselves
   * message ids (the §6.2 table). Resolving them here keeps the rule engine
   * free of intl — it decides WHICH concern is worth raising; this decides how
   * to say it in the reader's language.
   */
  const textOf = (item: RecruitSuggestion): string => {
    const values: Record<string, string | number> = { ...item.values };
    if (typeof values.concernId === 'string') values.concern = message(values.concernId);
    if (typeof values.answerId === 'string') values.answer = message(values.answerId);
    return message(item.messageId, values);
  };

  // 天數 —— 這條規則是憑什麼成立的。它現在住在名字旁邊,而不是右上角那顆剔格
  // 裡:剔格是一個「按下去會發生事情」的控制項,上面寫著「26 天」等於讓它同時
  // 當顯示器和按鈕,兩邊都說不清楚。剔格現在只寫「完成」。
  //
  // 而且要有主詞:光一個「26 天」不會告訴你那是等了 26 天還是停了 26 天,而這
  // 兩件事要做的動作剛好相反。訊號已經知道是哪一種 —— 有動作的那條是「他 1 天
  // 前做了什麼」,落後的那條是「這段對話停了 26 天」。
  const daysOf = (item: RecruitSuggestion): string => {
    const days = item.values.days;
    if (typeof days === 'number' && days > 0) {
      const signal = SIGNAL_OF[item.kind];
      if (signal === 'acted') return message('clientv2-recruit-suggest-days-ago', { days });
      if (signal === 'behind') return message('clientv2-recruit-suggest-days-stalled', { days });
      return message('clientv2-recruit-suggest-days', { days });
    }
    return message('clientv2-recruit-suggest-pending');
  };

  return (
    <section className="clientv2-card clientv2-queue" aria-labelledby="clientv2-recruit-suggest-title">
      <header
        className={`clientv2-queue-head${expandable ? ' is-expandable' : ''}`}
        data-testid="recruit-suggest-head"
        onClick={() => expandable && setExpanded(value => !value)}
      >
        <div className="clientv2-queue-heading">
          <span className="clientv2-queue-heading-icon" aria-hidden="true">
            <Briefcase size={20} />
          </span>
          <span>
            <span className="clientv2-queue-title" id="clientv2-recruit-suggest-title">
              {/* The count of what is actually on the card, not the cap. The
                  engine returns fewer than three when the book is empty (only
                  the two book-level rules fire) or at target with nothing to
                  chase (only the last one) — and a title saying 3 over a meter
                  saying 0 / 1 is the card contradicting itself. */}
              {message('clientv2-recruit-suggest-title', { count: suggestions.length })}
              {allDone && (
                <span className="clientv2-queue-celebrate" data-testid="recruit-suggest-celebrate">
                  <PartyPopper size={12} aria-hidden="true" />
                  {message('clientv2-queue-all-done')}
                </span>
              )}
            </span>
            {!allDone && (
              <span className="clientv2-queue-progress-label" data-testid="recruit-suggest-progress">
                {message('clientv2-queue-progress', { done: doneCount, total: suggestions.length })}
              </span>
            )}
          </span>
        </div>

        <div className="clientv2-queue-controls">
          <span className={`clientv2-queue-progress${allDone ? ' is-complete' : ''}`} aria-hidden="true">
            <span>
              <i style={{ width: `${animatedProgress}%` }} />
            </span>
            <b>{animatedProgress}%</b>
          </span>
          <button
            type="button"
            className="clientv2-queue-control"
            data-testid="recruit-suggest-expand"
            // Its own words: this control was announcing 「收合今日重點跟進」,
            // which is the other card's name and the only part a screen reader
            // hears.
            aria-label={message(expanded ? 'clientv2-recruit-suggest-collapse' : 'clientv2-recruit-suggest-expand')}
            title={message(expanded ? 'clientv2-recruit-suggest-collapse' : 'clientv2-recruit-suggest-expand')}
            aria-expanded={expanded}
            aria-controls="clientv2-recruit-suggest-tasks"
            disabled={!expandable}
            onClick={event => {
              // stopPropagation, or the bubbling header click toggles a second
              // time and the button appears to do nothing.
              event.stopPropagation();
              setExpanded(value => !value);
            }}
          >
            {expanded ? <ChevronUp size={17} aria-hidden="true" /> : <ChevronDown size={17} aria-hidden="true" />}
          </button>
        </div>
      </header>

      <div
        id="clientv2-recruit-suggest-tasks"
        className={`clientv2-queue-tasks-wrap${expanded ? ' is-open' : ''}`}
        aria-hidden={!expanded}
      >
        <div className="clientv2-queue-items" data-testid="recruit-suggestions">
          {suggestions.map(item => {
            const signal = SIGNAL_OF[item.kind];
            const isDone = Boolean(done[item.id]);
            const client = item.client;
            const name = client ? clientNameOf(client) || '-' : '';
            const tier = client ? tierMetaOf(client, adviserEmail) : null;
            const phone = client
              ? adviserFactsheetOf(client, adviserEmail)?.phoneNumber || client.phoneNumber || ''
              : '';
            const title = message(KIND_TITLE[item.kind]);
            // 顏色講的話,讀屏聽不到 —— 訊號那個詞改在這裡交代。
            const signalWord = message(SIGNAL_LABEL[signal]);

            return (
              <article
                data-testid="recruit-suggestion"
                data-kind={item.kind}
                className={`clientv2-queue-task${isDone ? ' is-done' : ''}`}
                key={item.id}
              >
                {/* 訊號藥丸拿掉了 —— 顏色搬到下面那行標題上。
                    原本一張卡把同一件事講四次:「有動作」、「做完職涯測驗」、
                    「Mary So 1 天前做完職涯測驗 − 談結果」、「1 天」。分類詞是
                    其中最不具體的一個,而它佔著整張卡讀第一眼的位置。現在顏色給
                    緊急度、標題給是什麼事、句子給怎麼開口,一層做一件事。
                    藥丸的字仍然留在讀屏標籤裡(下面 aria-label 的 signalWord)。 */}
                <div className="clientv2-queue-task-top">
                  {/* 這張卡是關於什麼事 —— 搬到左上角,也就是一張卡最先被讀到的
                      地方。它原本躲在名字底下,而那個位置屬於「這是誰」。 */}
                  <span className="clientv2-queue-task-title" style={{ color: SIGNAL_COLOR[signal] }}>
                    <i className="clientv2-queue-task-dot" style={{ backgroundColor: SIGNAL_COLOR[signal] }} />
                    {title}
                  </span>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={isDone}
                    className={`clientv2-queue-due${isDone ? ' is-done' : ''}`}
                    aria-label={message(isDone ? 'clientv2-queue-mark-pending' : 'clientv2-queue-mark-done', {
                      name: name || title
                    })}
                    onClick={() => setDone(value => ({ ...value, [item.id]: !isDone }))}
                  >
                    {isDone ? <SquareCheck size={15} aria-hidden="true" /> : <Square size={15} aria-hidden="true" />}
                    {/* 剔格說的是按下去會變成什麼,不是天數:一個寫著「26 天」的
                        方格,看起來像一個讀數,不像一個可以打勾的東西。 */}
                    {message(isDone ? 'clientv2-queue-done' : 'clientv2-recruit-suggest-mark')}
                  </button>
                </div>

                {client ? (
                  <button
                    type="button"
                    data-testid="recruit-suggestion-client"
                    className="clientv2-queue-client"
                    aria-label={`${signalWord} · ${title}: ${name}`}
                    onClick={() => onSelectClient(client._id)}
                  >
                    <span className="clientv2-row-avatar-wrap" aria-hidden="true">
                      <span className={`clientv2-row-avatar clientv2-av-${avatarGenderClass(client, adviserEmail)}`}>
                        {name.charAt(0)}
                      </span>
                      {tier && <span className={`clientv2-row-tier tier-${tier.cls}`}>{tier.key}</span>}
                    </span>
                    <span className="clientv2-queue-client-copy">
                      <span className="clientv2-queue-name">{name}</span>
                      {/* 天數住在名字旁邊:它是這條規則憑什麼成立的證據,和
                          「這是誰」讀在同一行才有意思。 */}
                      <span className="clientv2-queue-when">{daysOf(item)}</span>
                    </span>
                  </button>
                ) : (
                  // The two book-level rows are about the book, not a person.
                  // Same block, same rhythm — an icon where the avatar goes,
                  // and nothing to open, so it is not a button.
                  <div className="clientv2-queue-client is-book" data-testid="recruit-suggestion-book">
                    <span className="clientv2-row-avatar-wrap" aria-hidden="true">
                      <span className="clientv2-row-avatar clientv2-av-unspecified">
                        <Search size={16} aria-hidden="true" />
                      </span>
                    </span>
                    <span className="clientv2-queue-client-copy">
                      <span className="clientv2-queue-name">{message('clientv2-recruit-suggest-book')}</span>
                      {/* 天數住在名字旁邊:它是這條規則憑什麼成立的證據,和
                          「這是誰」讀在同一行才有意思。 */}
                      <span className="clientv2-queue-when">{daysOf(item)}</span>
                    </span>
                  </div>
                )}

                <p className="clientv2-queue-reason">{textOf(item)}</p>

                {item.kind === 'pausedLong' && client && (
                  <div className="clientv2-recruit-suggestion-actions">
                    {onRecontact && (
                      <button type="button" data-testid="recruit-paused-recontact" onClick={() => onRecontact(client)}>
                        {message('clientv2-recruit-paused-recontact')}
                      </button>
                    )}
                    {onRemoveFromRecruitment && (
                      <button
                        type="button"
                        data-testid="recruit-paused-remove"
                        onClick={() => onRemoveFromRecruitment(client)}
                      >
                        {message('clientv2-recruit-paused-remove')}
                      </button>
                    )}
                  </div>
                )}

                <div className="clientv2-queue-task-foot">
                  <span>{phone}</span>
                  <button
                    type="button"
                    className="clientv2-queue-follow"
                    data-testid="recruit-suggestion-cta"
                    onClick={() => onSuggestion(item)}
                  >
                    {message(item.ctaMessageId)}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default injectIntl(RecruitSuggestions);
